import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { AppLayout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Palette, 
  Sparkles, 
  Heart, 
  TrendingUp,
  Radio,
  Tv,
  Film,
  ImageIcon,
  FileText,
  ArrowRight
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const mediumIcons = {
    radio: Radio,
    tv: Tv,
    reel: Film,
    story: ImageIcon,
    post: FileText
  };

  const mediumNames = {
    radio: 'Radio',
    tv: 'TV',
    reel: 'Reels',
    story: 'Stories',
    post: 'Posts'
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-lime-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
        {/* Welcome Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
              {t('welcome')}, <span className="text-lime-500">{user?.name?.split(' ')[0]}</span>
            </h1>
            <p className="text-zinc-400 mt-1">
              {language === 'es' ? 'Aquí está el resumen de tu actividad' : "Here's your activity summary"}
            </p>
          </div>
          <Button 
            onClick={() => navigate('/generator')}
            className="bg-lime-500 text-black hover:bg-lime-600 font-medium gap-2"
            data-testid="generate-new-btn"
          >
            <Sparkles className="w-4 h-4" />
            {language === 'es' ? 'Nueva Idea' : 'New Idea'}
          </Button>
        </div>

        {/* Stats Cards - Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-zinc-900/50 border-zinc-800 hover:border-lime-500/30 transition-colors group">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase text-zinc-500 tracking-wider">{t('totalBrands')}</p>
                  <p className="text-4xl font-bold text-white mt-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {stats?.total_brands || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-lime-500/10 rounded-lg flex items-center justify-center group-hover:bg-lime-500/20 transition-colors">
                  <Palette className="w-6 h-6 text-lime-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800 hover:border-lime-500/30 transition-colors group">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase text-zinc-500 tracking-wider">{t('totalIdeas')}</p>
                  <p className="text-4xl font-bold text-white mt-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {stats?.total_ideas || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-lime-500/10 rounded-lg flex items-center justify-center group-hover:bg-lime-500/20 transition-colors">
                  <Sparkles className="w-6 h-6 text-lime-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800 hover:border-red-500/30 transition-colors group">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase text-zinc-500 tracking-wider">{t('favorites')}</p>
                  <p className="text-4xl font-bold text-white mt-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {stats?.total_favorites || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center group-hover:bg-red-500/20 transition-colors">
                  <Heart className="w-6 h-6 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800 hover:border-lime-500/30 transition-colors group">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase text-zinc-500 tracking-wider">{t('ideasThisWeek')}</p>
                  <p className="text-4xl font-bold text-white mt-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {stats?.recent_ideas || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-lime-500/10 rounded-lg flex items-center justify-center group-hover:bg-lime-500/20 transition-colors">
                  <TrendingUp className="w-6 h-6 text-lime-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Ideas by Medium */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white">
                {language === 'es' ? 'Ideas por Medio' : 'Ideas by Medium'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(mediumIcons).map(([medium, Icon]) => {
                  const count = stats?.ideas_by_medium?.[medium] || 0;
                  const total = stats?.total_ideas || 1;
                  const percentage = Math.round((count / total) * 100) || 0;
                  
                  return (
                    <div key={medium} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-zinc-400" />
                          <span className="text-zinc-300">{mediumNames[medium]}</span>
                        </div>
                        <span className="text-zinc-500">{count}</span>
                      </div>
                      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-lime-500 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white">
                {language === 'es' ? 'Acciones Rápidas' : 'Quick Actions'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-between bg-zinc-800/50 border-zinc-700 hover:bg-zinc-800 hover:border-lime-500/30 text-zinc-300"
                onClick={() => navigate('/brands')}
                data-testid="quick-brands"
              >
                <span className="flex items-center gap-2">
                  <Palette className="w-4 h-4 text-lime-500" />
                  {language === 'es' ? 'Gestionar Marcas' : 'Manage Brands'}
                </span>
                <ArrowRight className="w-4 h-4" />
              </Button>
              
              <Button 
                variant="outline" 
                className="w-full justify-between bg-zinc-800/50 border-zinc-700 hover:bg-zinc-800 hover:border-lime-500/30 text-zinc-300"
                onClick={() => navigate('/generator')}
                data-testid="quick-generator"
              >
                <span className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-lime-500" />
                  {language === 'es' ? 'Generar Copy' : 'Generate Copy'}
                </span>
                <ArrowRight className="w-4 h-4" />
              </Button>
              
              <Button 
                variant="outline" 
                className="w-full justify-between bg-zinc-800/50 border-zinc-700 hover:bg-zinc-800 hover:border-red-500/30 text-zinc-300"
                onClick={() => navigate('/library?favorites=true')}
                data-testid="quick-favorites"
              >
                <span className="flex items-center gap-2">
                  <Heart className="w-4 h-4 text-red-500" />
                  {language === 'es' ? 'Ver Favoritos' : 'View Favorites'}
                </span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
